import { Button, Dropdown, message } from 'antd';
import { MenuProps } from 'antd';
import { FC, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { MenuOutlined } from '@ant-design/icons';
import { useToggle } from 'react-use';

import { Feature } from '../../../shared/models/features';
import { translationNamespace } from '../../constants/translation-resources';
import { Preset } from '../../models/preset';
import { ConfirmationModal } from '../../../shared/components/confirmation-modal';
import { useDeletePresetMutation } from '../../data/mutations/preset-mutations';
import { permissionSelector } from '../../../shared/data/store/selectors/auth-selectors';
import { AppResources } from '../../../shared/constants/app-resources';

interface ActionsProps {
  preset: Preset;
}

export const Actions: FC<ActionsProps> = ({ preset }) => {
  const { t } = useTranslation(translationNamespace);
  const deleteMutation = useDeletePresetMutation();
  const permissions = useSelector(permissionSelector);
  const [showConfirmDeleteModal, toggleShowConfirmDeleteModal] = useToggle(false);

  const remove = useCallback(() => {
    if (preset.id == null) {
      return;
    }

    deleteMutation.mutate(preset.id, {
      onSuccess: () => {
        message.success(t('presets.shared.deleteSuccessMessage'));
        toggleShowConfirmDeleteModal();
      },
    });
  }, [deleteMutation, t, preset.id, toggleShowConfirmDeleteModal]);

  const items = useMemo<MenuProps['items']>(() => {
    const menuItems: MenuProps['items'] = [];

    if (permissions[AppResources.PRESET]['UPDATE']) {
      menuItems.push({
        key: 'edit',
        label: <Link to={`presets/${preset.id}`}>{t(`${Feature.SHARED}:edit`)}</Link>,
      });
    }
    if (permissions[AppResources.PRESET]['DELETE']) {
      menuItems.push({
        key: 'remove',
        onClick: () => toggleShowConfirmDeleteModal(),
        label: t(`${Feature.SHARED}:remove`),
      });
    }

    return menuItems;
  }, [permissions, preset.id, t, toggleShowConfirmDeleteModal]);

  return (
    <>
      <Dropdown menu={{ items }} trigger={['click']}>
        <Button type="link" icon={<MenuOutlined />}>
          {t(`${Feature.SHARED}:actions`)}
        </Button>
      </Dropdown>
      <ConfirmationModal
        open={showConfirmDeleteModal}
        title={t(`${Feature.SHARED}:areYouSure`)}
        onCancel={toggleShowConfirmDeleteModal}
        onOk={remove}
        content={t(`${Feature.SHARED}:confirmDeleteMsg`, {
          resourceName: preset.name,
          buttonTitle: t(`${Feature.SHARED}:confirm`),
        })}
      />
    </>
  );
};
